import { useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    confirm,
    Description,
    DialogRoot,
    DialogTrigger,
    FieldGroup,
    Group,
    HiddenDisclosureSection,
    Label,
    TextInput,
    toast,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import {
    AdaptiveContentBuilderDialog,
    AdaptiveContentFeedbackButton,
    DocumentationLink,
} from '../../../../../Components';
import { NavigationRedirect, useNavigation, useNavigationState } from '../../../../../navigation';
import { apiClient, apiQuery, useSiteContext } from '../../../../../state';

/**
 * Section shown in audience settings for adaptive content.
 */
export function AdaptiveContentSettings() {
    const { site, organizationId } = useSiteContext();
    const state = useNavigationState();

    const isAdaptiveSchemaOpen =
        state.screen.type === 'site' &&
        state.screen.tab?.type === 'settings' &&
        state.screen.tab?.section === 'schema';

    if (isAdaptiveSchemaOpen && !site.adaptiveContent?.enabled) {
        return (
            <NavigationRedirect
                to={{
                    current: {
                        tab: {
                            type: 'settings',
                            section: 'audience',
                        },
                    },
                }}
                replace
            />
        );
    }

    return (
        <AdaptiveContentSettingsSection
            site={site}
            organizationId={organizationId}
            isSchemaOpen={isAdaptiveSchemaOpen}
        />
    );
}

function AdaptiveContentSettingsSection(props: {
    site: api.Site;
    organizationId: string;
    isSchemaOpen: boolean;
}) {
    const { site, organizationId, isSchemaOpen } = props;

    const { data: sitePublishingAuth } = useSuspenseQuery(
        apiQuery('getSitePublishingAuthById', { organizationId, siteId: site.id })
    );

    return (
        <div className="flex flex-col gap-3">
            <AdaptiveContentSchemaSection
                organizationId={organizationId}
                site={site}
                isOpen={isSchemaOpen}
            />
            <AdaptiveContentAdvancedSettingsSection
                organizationId={organizationId}
                site={site}
                sitePublishingAuth={sitePublishingAuth}
            />
        </div>
    );
}

function AdaptiveContentAdvancedSettingsSection(props: {
    organizationId: string;
    site: api.Site;
    sitePublishingAuth: api.SitePublishingAuth;
}) {
    return (
        <div className="flex w-full flex-col">
            <HiddenDisclosureSection
                labels={{
                    show: 'Show advanced options',
                    hide: 'Hide advanced options',
                }}
            >
                <div className="flex flex-col gap-5">
                    <AdaptiveContentVisitorTokenInfoSection {...props} />
                    <AdaptiveContentDisableSection {...props} />
                </div>
            </HiddenDisclosureSection>
            <div className="ml-auto">
                <AdaptiveContentFeedbackButton />
            </div>
        </div>
    );
}

/**
 * Section of the adaptive content settings to edit the JSON schema of the expected attributes.
 */
function AdaptiveContentSchemaSection(props: {
    site: api.Site;
    organizationId: string;
    isOpen: boolean;
}) {
    const { organizationId, site, isOpen } = props;
    const { navigate } = useNavigation();

    return (
        <FieldGroup orientation="horizontal">
            <Group orientation="vertical">
                <Label>Visitor schema</Label>
                <Description>
                    To adapt content to your visitors, you need to pass along data about them. The
                    adaptive schema defines what data you will send over, so you can then use it in
                    your content.
                </Description>
            </Group>

            <DialogRoot
                isOpen={isOpen}
                onOpenChange={(isOpen) => {
                    isOpen
                        ? navigate({
                              current: {
                                  tab: {
                                      type: 'settings',
                                      section: 'schema',
                                  },
                              },
                          })
                        : navigate({
                              current: {
                                  tab: {
                                      type: 'settings',
                                      section: 'audience',
                                  },
                              },
                          });
                }}
            >
                <DialogTrigger>
                    <Button
                        size="medium"
                        kind="secondary"
                        tooltip={{
                            label: 'Edit the schema to adapt your content to your visitors',
                        }}
                        label="Edit schema"
                        className="shrink-0"
                    />
                </DialogTrigger>
                <AdaptiveContentBuilderDialog
                    initialTab="schema"
                    organizationId={organizationId}
                    siteId={site.id}
                    tabs={[{ tab: 'schema' }]}
                />
            </DialogRoot>
        </FieldGroup>
    );
}

/**
 * Section of the adaptive content settings that shows the signing key for the visitor token.
 */
function AdaptiveContentVisitorTokenInfoSection(props: {
    site: api.Site;
    organizationId: string;
    sitePublishingAuth: api.SitePublishingAuth;
}) {
    const { site, organizationId, sitePublishingAuth } = props;

    return (
        <Group>
            <Label>Visitor token signing key</Label>
            <Description>
                To enable secure transmission of visitor data, it should be stored as a signed JSON
                Web Token. Make sure to sign the token with the key below so we can read it when
                they visit your site.{' '}
                <DocumentationLink path="publishing-documentation/adaptive-content" host="docs">
                    Learn more.
                </DocumentationLink>
            </Description>
            <div className="flex w-full items-center justify-center gap-2">
                <TextInput
                    size="medium"
                    readOnly
                    copyable={true}
                    value={sitePublishingAuth.privateKey}
                />

                <Button
                    label="Regenerate"
                    size="small"
                    kind="blank"
                    leadingIcon={Icon.Reset}
                    onPress={() => {
                        void confirm({
                            title: 'Regenerate private key?',
                            description:
                                'Regenerating the private key will require you to update your authentication backend. Are you sure you want to continue?',
                            kind: 'danger',
                            confirmLabel: 'Regenerate',
                            onConfirm: async (_event, { preventClose }) => {
                                await toast.catch(
                                    async () => {
                                        await apiClient.sitePublishingAuth.regenerateSitePublishingAuthById(
                                            organizationId,
                                            site.id
                                        );
                                        toast.success('Private key regenerated');
                                    },
                                    'Error while regenerating private key',
                                    { onReject: preventClose }
                                );
                            },
                        });
                    }}
                />
            </div>
        </Group>
    );
}

function AdaptiveContentDisableSection(props: { site: api.Site; organizationId: string }) {
    const { site, organizationId } = props;
    const [_, wrapLoading] = useLoadingState();

    const onDisable = async () => {
        await wrapLoading(async () => {
            await toast.catch(async () => {
                await apiClient.sites.updateSiteById(organizationId, site.id, {
                    adaptiveContent: {
                        enabled: false,
                    },
                });
                toast.success('Adaptive content disabled');
            }, 'Error while disabling adaptive content');
        });
    };

    return (
        <FieldGroup orientation="horizontal">
            <Group orientation="vertical">
                <Label>Disable adaptive content</Label>
                <Description>
                    Content on this site will no longer be adapted to visitors. Access conditions
                    you have set up on pages, sections and variants remain available to edit, but
                    will no longer apply to visitors.
                </Description>
            </Group>
            <Button
                size="medium"
                kind="danger"
                label="Disable adaptive content"
                className="shrink-0"
                onPress={onDisable}
            />
        </FieldGroup>
    );
}
